import { useMemo, useState } from "react";
import type { FormEvent } from "react";
import { Aviso, Botao, Campo, CampoLongo, Cartao, Chip } from "../ui";
import {
  quandoLegivel,
  ACAO_EM_BRANCO,
  GATILHO_EM_BRANCO,
  ROTULO_DA_EXECUCAO,
  ROTULO_DO_DESTINO_DE_EMAIL,
  ROTULO_DO_EVENTO_DE_GATILHO,
  ROTULO_DO_MOTIVO_DE_BLOQUEIO,
  ROTULO_DO_TIPO_DE_ACAO,
  corpoDoGatilho,
  formularioDoGatilho,
  resumirGatilho,
  useAlterarGatilho,
  useCriarGatilho,
  useExcluirGatilho,
  useExecucoesDeGatilhos,
  useGatilhos,
  useOrganizacoesParaAutomacao,
  validarGatilho,
  type EventoDeGatilho,
  type FormularioDeAcao,
  type FormularioDeGatilho,
  type Gatilho,
  type ModoDoGatilho,
} from "../../nucleo/automacao";
import { mensagemDoConsole } from "../../nucleo/console";
import {
  ROTULO_DA_URGENCIA,
  ROTULO_DO_ESTADO,
  useAgentesDeAtendimento,
  useEquipesDeAtendimento,
} from "../../nucleo/tickets";
import { CaixaDeMarcar, CampoDeEscolha } from "./apoio";
import { useVoltarParaHome } from "../../nucleo/voltarParaHome";

const OPCOES_DE_ESTADO = Object.entries(ROTULO_DO_ESTADO).map(([valor, rotulo]) => ({ valor, rotulo }));
const OPCOES_DE_URGENCIA = Object.entries(ROTULO_DA_URGENCIA).map(([valor, rotulo]) => ({ valor, rotulo }));
const OPCOES_DE_EVENTO = Object.entries(ROTULO_DO_EVENTO_DE_GATILHO).map(([valor, rotulo]) => ({ valor, rotulo }));
const OPCOES_DE_ACAO = Object.entries(ROTULO_DO_TIPO_DE_ACAO).map(([valor, rotulo]) => ({ valor, rotulo }));
const OPCOES_DE_DESTINO = Object.entries(ROTULO_DO_DESTINO_DE_EMAIL).map(([valor, rotulo]) => ({
  valor,
  rotulo: rotulo.charAt(0).toLocaleUpperCase("pt-BR") + rotulo.slice(1),
}));

function CampoDaAcao({
  indice,
  acao,
  aoMudar,
  equipes,
  agentes,
}: {
  indice: number;
  acao: FormularioDeAcao;
  aoMudar: (campo: keyof FormularioDeAcao, valor: string) => void;
  equipes: Array<{ valor: string; rotulo: string }>;
  agentes: Array<{ valor: string; rotulo: string }>;
}) {
  const n = indice + 1;
  return (
    <>
      <CampoDeEscolha rotulo={`Tipo da ação ${n}`} valor={acao.tipo} aoMudar={(v) => aoMudar("tipo", v)} opcoes={OPCOES_DE_ACAO} />
      {acao.tipo === "atribuir_equipe" ? (
        <CampoDeEscolha rotulo={`Equipe da ação ${n}`} valor={acao.equipe_id} aoMudar={(v) => aoMudar("equipe_id", v)} opcoes={equipes} vazio="Escolha a equipe" />
      ) : null}
      {acao.tipo === "atribuir_responsavel" ? (
        <CampoDeEscolha rotulo={`Responsável da ação ${n}`} valor={acao.responsavel_id} aoMudar={(v) => aoMudar("responsavel_id", v)} opcoes={agentes} vazio="Escolha o responsável" />
      ) : null}
      {acao.tipo === "mudar_urgencia" ? (
        <CampoDeEscolha rotulo={`Nova urgência da ação ${n}`} valor={acao.urgencia} aoMudar={(v) => aoMudar("urgencia", v)} opcoes={OPCOES_DE_URGENCIA} />
      ) : null}
      {acao.tipo === "mudar_estado" ? (
        <CampoDeEscolha rotulo={`Novo estado da ação ${n}`} valor={acao.estado} aoMudar={(v) => aoMudar("estado", v)} opcoes={OPCOES_DE_ESTADO} />
      ) : null}
      {acao.tipo === "nota_interna" ? (
        <CampoLongo rotulo={`Texto da nota da ação ${n}`} value={acao.texto} onChange={(e) => aoMudar("texto", e.target.value)} />
      ) : null}
      {acao.tipo === "enviar_email" ? (
        <>
          <CampoDeEscolha rotulo={`Destinatário do e-mail da ação ${n}`} valor={acao.destino} aoMudar={(v) => aoMudar("destino", v)} opcoes={OPCOES_DE_DESTINO} />
          {acao.destino === "endereco" ? (
            <Campo rotulo={`Endereço de e-mail da ação ${n}`} value={acao.endereco} onChange={(e) => aoMudar("endereco", e.target.value)} />
          ) : null}
          <Campo rotulo={`Assunto do e-mail da ação ${n}`} value={acao.assunto} onChange={(e) => aoMudar("assunto", e.target.value)} />
          <CampoLongo rotulo={`Texto do e-mail da ação ${n}`} value={acao.texto} onChange={(e) => aoMudar("texto", e.target.value)} />
        </>
      ) : null}
    </>
  );
}

export function PainelDeGatilhos() {
  const gatilhos = useGatilhos();
  const execucoes = useExecucoesDeGatilhos();
  const organizacoes = useOrganizacoesParaAutomacao();
  const equipes = useEquipesDeAtendimento();
  const agentes = useAgentesDeAtendimento();
  const criar = useCriarGatilho();
  const voltarParaHome = useVoltarParaHome();
  const alterar = useAlterarGatilho();
  const excluir = useExcluirGatilho();
  const [form, setForm] = useState<FormularioDeGatilho>(GATILHO_EM_BRANCO);
  const [emEdicao, setEmEdicao] = useState<string | null>(null);
  const [problema, setProblema] = useState<string | null>(null);
  const [confirmando, setConfirmando] = useState<string | null>(null);

  const nomes = useMemo(() => {
    const mapa: Record<string, string> = {};
    for (const o of organizacoes.data ?? []) mapa[o.id] = o.nome_fantasia ?? o.razao_social;
    for (const e of equipes.data ?? []) mapa[e.id] = e.nome;
    for (const a of agentes.data ?? []) mapa[a.id] = a.nome;
    return mapa;
  }, [organizacoes.data, equipes.data, agentes.data]);
  const nomesDosGatilhos = useMemo(
    () => Object.fromEntries((gatilhos.data ?? []).map((g) => [g.id, g.nome])),
    [gatilhos.data],
  );

  const opcoesDeEquipe = (equipes.data ?? []).map((e) => ({ valor: e.id, rotulo: e.nome }));
  const opcoesDeAgente = (agentes.data ?? []).map((a) => ({ valor: a.id, rotulo: a.nome }));

  const atualizar = <C extends keyof FormularioDeGatilho>(campo: C, valor: FormularioDeGatilho[C]) =>
    setForm((atual) => ({ ...atual, [campo]: valor }));
  const mudarAcao = (indice: number, campo: keyof FormularioDeAcao, valor: string) =>
    setForm((atual) => ({
      ...atual,
      acoes: atual.acoes.map((a, i) => (i === indice ? { ...a, [campo]: valor } : a)),
    }));

  function limpar() {
    setForm(GATILHO_EM_BRANCO);
    setEmEdicao(null);
    setProblema(null);
  }

  function editar(gatilho: Gatilho) {
    setForm(formularioDoGatilho(gatilho));
    setEmEdicao(gatilho.id);
    setProblema(null);
  }

  function submeter(evento: FormEvent) {
    evento.preventDefault();
    const erro = validarGatilho(form);
    setProblema(erro);
    if (erro) return;
    const corpo = corpoDoGatilho(form);
    if (emEdicao) alterar.mutate({ id: emEdicao, corpo }, { onSuccess: limpar });
    else {
      criar.mutate(corpo, {
        onSuccess: () => {
          limpar();
          voltarParaHome("Gatilho criado.");
        },
      });
    }
  }

  const falha = criar.error ?? alterar.error ?? excluir.error ?? gatilhos.error;
  const gravando = criar.isPending || alterar.isPending;

  return (
    <div>
      <Cartao titulo={emEdicao ? "Editar gatilho" : "Novo gatilho"} className="mb-4">
        <form onSubmit={submeter} data-testid="formulario-de-gatilho" noValidate>
          <Campo rotulo="Nome do gatilho" value={form.nome} onChange={(e) => atualizar("nome", e.target.value)} />
          <div className="flex gap-3 flex-wrap">
            <CampoDeEscolha rotulo="Quando" className="flex-1" valor={form.evento} aoMudar={(v) => atualizar("evento", v as EventoDeGatilho)} opcoes={OPCOES_DE_EVENTO} />
            <CampoDeEscolha
              rotulo="Combinar condições"
              className="flex-1"
              valor={form.modo}
              aoMudar={(v) => atualizar("modo", v as ModoDoGatilho)}
              opcoes={[
                { valor: "todas", rotulo: "Todas as condições" },
                { valor: "qualquer", rotulo: "Qualquer condição" },
              ]}
            />
          </div>
          <p className="campo__rotulo">Condições (deixe em branco o que não importa)</p>
          <div className="flex gap-3 flex-wrap">
            <CampoDeEscolha rotulo="Organização" className="flex-1" valor={form.organizacao_id} aoMudar={(v) => atualizar("organizacao_id", v)} opcoes={(organizacoes.data ?? []).map((o) => ({ valor: o.id, rotulo: o.nome_fantasia ?? o.razao_social }))} vazio="Qualquer organização" />
            <CampoDeEscolha rotulo="Urgência é" className="flex-1" valor={form.urgencia} aoMudar={(v) => atualizar("urgencia", v)} opcoes={OPCOES_DE_URGENCIA} vazio="Qualquer urgência" />
            <CampoDeEscolha rotulo="Estado é" className="flex-1" valor={form.estado} aoMudar={(v) => atualizar("estado", v)} opcoes={OPCOES_DE_ESTADO} vazio="Qualquer estado" />
          </div>
          <div className="flex gap-3 flex-wrap">
            <Campo rotulo="Categoria é" value={form.categoria} onChange={(e) => atualizar("categoria", e.target.value)} />
            <Campo rotulo="Serviço é" value={form.servico} onChange={(e) => atualizar("servico", e.target.value)} />
            <Campo rotulo="O texto contém" value={form.texto_contem} onChange={(e) => atualizar("texto_contem", e.target.value)} />
            <Campo rotulo="O chamado tem a tag" value={form.tag} onChange={(e) => atualizar("tag", e.target.value)} />
          </div>
          <p className="campo__rotulo">Então (as ações rodam na ordem)</p>
          {form.acoes.map((acao, indice) => (
            <fieldset key={indice} className="mb-3" data-testid={`acao-${indice + 1}`}>
              <legend className="nota-rodape">Ação {indice + 1}</legend>
              <CampoDaAcao indice={indice} acao={acao} aoMudar={(campo, valor) => mudarAcao(indice, campo, valor)} equipes={opcoesDeEquipe} agentes={opcoesDeAgente} />
              {form.acoes.length > 1 ? (
                <Botao type="button" variante="fantasma" tamanho="pequeno" onClick={() => setForm((a) => ({ ...a, acoes: a.acoes.filter((_, i) => i !== indice) }))}>
                  {`Remover ação ${indice + 1}`}
                </Botao>
              ) : null}
            </fieldset>
          ))}
          <Botao type="button" variante="fantasma" onClick={() => setForm((a) => ({ ...a, acoes: [...a.acoes, { ...ACAO_EM_BRANCO }] }))}>
            Adicionar ação
          </Botao>
          <div className="flex gap-3 flex-wrap mt-3">
            <Campo rotulo="Ordem de execução" type="number" min={0} value={form.ordem} onChange={(e) => atualizar("ordem", e.target.value)} ajuda="Menor número roda primeiro." />
          </div>
          <CaixaDeMarcar rotulo="Gatilho ativo" marcada={form.ativo} aoMudar={(v) => atualizar("ativo", v)} />
          {problema ? <Aviso tom="erro" className="mb-3">{problema}</Aviso> : null}
          {falha ? <Aviso tom="erro" className="mb-3">{mensagemDoConsole(falha)}</Aviso> : null}
          <div className="flex gap-2">
            <Botao type="submit" disabled={gravando}>{gravando ? "Salvando…" : "Salvar gatilho"}</Botao>
            {emEdicao ? <Botao type="button" variante="fantasma" onClick={limpar}>Cancelar edição</Botao> : null}
          </div>
        </form>
      </Cartao>

      <section aria-labelledby="titulo-gatilhos" className="mb-5">
        <h2 className="titulo-secao" id="titulo-gatilhos">Gatilhos, na ordem de execução</h2>
        {gatilhos.isPending ? <p role="status">Carregando gatilhos…</p> : null}
        {gatilhos.data && gatilhos.data.length === 0 ? <Aviso tom="marca">Nenhum gatilho cadastrado.</Aviso> : null}
        <div className="lista-de-itens" data-testid="lista-de-gatilhos">
          {gatilhos.data?.map((gatilho) => (
            <Cartao
              key={gatilho.id}
              titulo={gatilho.nome}
              data-testid={`gatilho-${gatilho.nome}`}
              acessorio={<Chip estado={gatilho.ativo ? "ok" : "neutro"}>{gatilho.ativo ? `Ativo · ordem ${gatilho.ordem}` : "Desativado"}</Chip>}
            >
              <p>{resumirGatilho(gatilho, nomes)}</p>
              <div className="flex gap-2 mt-2 flex-wrap">
                <Botao type="button" variante="fantasma" aria-label={`Editar gatilho ${gatilho.nome}`} onClick={() => editar(gatilho)}>Editar</Botao>
                <Botao type="button" variante="fantasma" aria-label={`${gatilho.ativo ? "Desativar" : "Ativar"} gatilho ${gatilho.nome}`} onClick={() => alterar.mutate({ id: gatilho.id, corpo: { ativo: !gatilho.ativo } })}>
                  {gatilho.ativo ? "Desativar" : "Ativar"}
                </Botao>
                {confirmando === gatilho.id ? (
                  <Botao type="button" aria-label={`Confirmar exclusão do gatilho ${gatilho.nome}`} onClick={() => excluir.mutate(gatilho.id, { onSettled: () => setConfirmando(null) })}>
                    Confirmar exclusão
                  </Botao>
                ) : (
                  <Botao type="button" variante="fantasma" aria-label={`Excluir gatilho ${gatilho.nome}`} onClick={() => setConfirmando(gatilho.id)}>Excluir</Botao>
                )}
              </div>
            </Cartao>
          ))}
        </div>
      </section>

      <section aria-labelledby="titulo-execucoes">
        <h2 className="titulo-secao" id="titulo-execucoes">Últimas execuções</h2>
        {execucoes.data && execucoes.data.length === 0 ? <p className="nota-rodape">Nenhum gatilho executou ainda.</p> : null}
        <ul data-testid="lista-de-execucoes">
          {execucoes.data?.map((item, indice) => {
            const gatilhoId = String(item.detalhe.gatilho_id ?? "");
            const nome = String(item.detalhe.gatilho_nome ?? nomesDosGatilhos[gatilhoId] ?? "Gatilho");
            const motivo = typeof item.detalhe.motivo === "string" ? ROTULO_DO_MOTIVO_DE_BLOQUEIO[item.detalhe.motivo] ?? item.detalhe.motivo : null;
            const mensagem = item.tipo === "gatilho_falhou" ? String(item.detalhe.mensagem ?? "") : motivo;
            return (
              <li key={`${item.ticket_id}-${indice}`}>
                {`Chamado #${item.numero_do_ticket ?? "?"} · ${nome} · ${ROTULO_DA_EXECUCAO[item.tipo] ?? item.tipo}${mensagem ? ` — ${mensagem}` : ""} · ${quandoLegivel(item.ocorrido_em)}`}
              </li>
            );
          })}
        </ul>
      </section>
    </div>
  );
}
